import { connect } from 'react-redux';
import React from 'react';
import { Alert, Button, Input } from 'antd';
import { withTranslation, WithTranslation } from 'react-i18next';
import { ApplicationState } from '../../reducers';
import { AsyncDispatch } from '../../../types/global';
import {
  ListUser,
  UserType,
  PermissionGroup,
  UserPermissionRole,
  MultiAccountUser,
  AdminAccount,
} from '../../../types/user';
import {
  fetchUsers,
  updateUserState,
  fetchMappedUserRoles,
  fetchAdminUsersView,
} from '../../actions/user';
import { showUpgradePlanNotification } from '../../actions/app/modal';
import { withContainerWrapper } from '../ContainerWrapper';
import UserTable from '../../components/body/user/UserTable';
import MultiAccountUserTable from '../../components/body/user/MultiAccountUserTable';
import UserDrawer from '../../components/body/user/UserDrawer';
import MultiAccountUserDrawer from '../../components/body/user/MultiAccountUserDrawer';
import UserRoleDrawer from '../../components/body/user/UserRoleDrawer';
import { Segment, StandardResourceCode } from '../../../types/resources';
import { isReceiver } from '../../utils/UserUtils';
import { intercomEvent } from '../../utils/IntercomUtils';
import { hasPermission } from '../../utils/Permissions';

type UserContainerProps = {
  dispatch: AsyncDispatch;
  user: UserType;
  userList: ListUser[];
  multiAccountUsers: MultiAccountUser[];
  adminAccounts: AdminAccount[];
  fetchingAdminUsersView: boolean;
  permissionGroups: PermissionGroup[];
  roles: UserPermissionRole[];
  segments: Segment[];
  maintenanceTypes: StandardResourceCode[];
  isReceiver: boolean;
  isMultiAccountAdmin: boolean;
} & WithTranslation;

type UserContainerState = {
  showCreateDrawer: boolean;
  showEditDrawer: boolean;
  showRoleDrawer: boolean;
  selectedUserId: number | null;
  filterKeywords: string;
};

export class UserContainer extends React.Component<UserContainerProps, UserContainerState> {
  constructor(props: UserContainerProps) {
    super(props);
    this.state = {
      showCreateDrawer: false,
      showEditDrawer: false,
      showRoleDrawer: false,
      selectedUserId: null,
      filterKeywords: '',
    };
  }

  componentDidMount() {
    this.fetchUsers();
    intercomEvent('viewed-company-user-settings');
  }

  componentDidUpdate(prevProps: UserContainerProps) {
    // Refetch users if multi-account admin status changes or if user list is empty
    const { isMultiAccountAdmin, multiAccountUsers, userList } = this.props;

    if (prevProps.isMultiAccountAdmin !== isMultiAccountAdmin) {
      this.fetchUsers();
    }

    // If multi-account admin but list is empty, refetch
    if (
      isMultiAccountAdmin &&
      multiAccountUsers.length === 0 &&
      !this.props.fetchingAdminUsersView
    ) {
      this.fetchUsers();
    }

    // If single-account but list is empty, refetch
    if (!isMultiAccountAdmin && userList.length === 0) {
      this.fetchUsers();
    }
  }

  fetchUsers = () => {
    const { isMultiAccountAdmin } = this.props;

    if (isMultiAccountAdmin) {
      // Fetch users across all admin's accounts
      this.props.dispatch(fetchAdminUsersView());
    } else {
      // Fetch users for current account only
      this.props.dispatch(fetchUsers());
    }
  };

  updateUserState = (active: number) => {
    const { selectedUserId: id } = this.state;
    return this.props.dispatch(updateUserState(id!, active));
  };

  showCreateDrawer = () => {
    const { user } = this.props;
    if (!hasPermission(user, 'can_manage_users'))
      this.props.dispatch(showUpgradePlanNotification());

    this.setState({ showCreateDrawer: true });

    intercomEvent('viewed-company-user-settings', { action: 'add_new' });
  };

  showRoleDrawer = () => {
    const { user } = this.props;
    if (!hasPermission(user, 'can_manage_users'))
      this.props.dispatch(showUpgradePlanNotification());

    this.setState({ showRoleDrawer: true });
    intercomEvent('viewed-company-user-settings', { action: 'manage_roles' });
  };

  showEditDrawer = (userId: number) => {
    const { user, isMultiAccountAdmin } = this.props;
    if (!hasPermission(user, 'can_manage_users'))
      this.props.dispatch(showUpgradePlanNotification());

    this.setState({ showEditDrawer: true, selectedUserId: userId });

    // Only fetch mapped user roles for single-account mode
    if (!isMultiAccountAdmin) {
      this.props.dispatch(fetchMappedUserRoles(userId));
    }

    intercomEvent('viewed-company-user-settings', { action: 'edit' });
  };

  closeDrawer = (shouldRefresh?: boolean) => {
    this.setState({ showCreateDrawer: false, showEditDrawer: false, selectedUserId: null });
    if (shouldRefresh) {
      this.fetchUsers();
    }
  };

  closeRoleDrawer = () => {
    this.setState({ showRoleDrawer: false });
  };

  render() {
    const { userList, multiAccountUsers, adminAccounts, isReceiver, isMultiAccountAdmin, t } =
      this.props;
    const { showCreateDrawer, showEditDrawer, showRoleDrawer, selectedUserId } = this.state;

    const selectedMultiAccountUser = isMultiAccountAdmin
      ? multiAccountUsers.find(u => u.id === selectedUserId)
      : undefined;
    const selectedUser = !isMultiAccountAdmin
      ? userList.find(user => user.id === selectedUserId)
      : undefined;

    return (
      <div className="user__wrapper">
        <div className="page-layout__top-bar">
          <div className="page-layout__top-bar__container">
            <div className="flex-1">
              <Input.Search
                className="user__search-input"
                value={this.state.filterKeywords}
                onChange={e => this.setState({ filterKeywords: e.target.value })}
                placeholder={t('user:search')}
                allowClear
              />
              {isMultiAccountAdmin && adminAccounts.length > 1 && (
                <span className="ml-6">
                  <Alert
                    message={t('user:viewingMultipleAccounts')}
                    style={{ display: 'inline-flex', padding: '4px 8px' }}
                    type="info"
                    showIcon
                  />
                  <span className="text-sm text-gray-600 ml-2">
                    {t('user:accountCount', { count: adminAccounts.length })}
                  </span>
                </span>
              )}
            </div>
            <div className="page-layout__top-bar__actions">
              {!isReceiver && !isMultiAccountAdmin && (
                <Button className="user__role-button" onClick={() => this.showRoleDrawer()}>
                  {t('user:manageRoles')}
                </Button>
              )}
              {!isMultiAccountAdmin && (
                <Button type="primary" onClick={() => this.showCreateDrawer()}>
                  {t('user:addUser')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {isMultiAccountAdmin ? (
          <MultiAccountUserTable
            userList={multiAccountUsers}
            adminAccounts={adminAccounts}
            editUser={this.showEditDrawer}
            filterKeywords={this.state.filterKeywords}
          />
        ) : (
          <UserTable
            userList={userList}
            editUser={this.showEditDrawer}
            filterKeywords={this.state.filterKeywords}
            roles={this.props.roles}
          />
        )}

        {isMultiAccountAdmin ? (
          <MultiAccountUserDrawer
            visible={showEditDrawer}
            onClose={() => this.closeDrawer()}
            user={selectedMultiAccountUser}
          />
        ) : (
          <UserDrawer
            visible={showCreateDrawer || showEditDrawer}
            onClose={() => this.closeDrawer()}
            currentUserId={this.props.user.id}
            user={selectedUser}
            updateUserState={this.updateUserState}
            showRoleDrawer={this.showRoleDrawer}
          />
        )}

        {showRoleDrawer && !isMultiAccountAdmin && (
          <UserRoleDrawer
            visible={showRoleDrawer}
            onClose={() => this.closeRoleDrawer()}
            permissionGroups={this.props.permissionGroups}
            roles={this.props.roles}
            segments={this.props.segments}
            maintenanceTypes={this.props.maintenanceTypes}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => {
  const user = state.user.user;
  const adminAccounts = state.user.adminAccounts || [];
  const accessibleAccounts = state.user.accessibleAccounts?.accessible_accounts || [];

  // User is multi-account admin if they have admin access to multiple accounts
  const isMultiAccountAdmin =
    user &&
    !isReceiver(user) &&
    (adminAccounts.length > 1 ||
      (accessibleAccounts.length > 1 && accessibleAccounts.some(acc => acc.is_admin)));

  return {
    user,
    userList: state.user.userList,
    multiAccountUsers: state.user.multiAccountUsers,
    adminAccounts,
    fetchingAdminUsersView: state.user.fetchingAdminUsersView,
    permissionGroups: state.user.permissionGroups,
    roles: state.user.roles,
    segments: state.resources.data.global.segments.filter(s => s.name !== 'app_inheriting'),
    maintenanceTypes: state.resources.data.global.maintenance_types,
    isReceiver: user && isReceiver(user),
    isMultiAccountAdmin,
  };
};

export default withTranslation()(connect(mapStateToProps)(withContainerWrapper(UserContainer)));
